import { sameRef, sameScope, type Diagnostic, type Ref, type Scope } from "../work-os/primitives.ts";
import type { LocalNavigationDefinition } from "./spec.ts";

/** Validate local references only. A valid menu never grants access to a target surface or data. */
export function checkNavigationReferences(nav: LocalNavigationDefinition, refs: Ref[]): Diagnostic[] {
  const errors: Diagnostic[] = [];
  const add = (code: string, path: string) => errors.push({ code, path });
  const entries = new Map(nav.entries.map(entry => [entry.id, entry]));
  if (entries.size !== nav.entries.length) add("DUPLICATE_NAVIGATION", "entries");
  for (const entry of nav.entries) {
    if (!refs.some(ref => sameRef(ref, entry.surfaceRef))) add("SURFACE_UNAVAILABLE", entry.id);
    const seen = new Set<string>();
    let current: string | null = entry.id;
    while (current !== null) {
      if (seen.has(current)) { add("NAVIGATION_CYCLE", entry.id); break; }
      seen.add(current);
      const parent = entries.get(current);
      if (!parent) { add("NAVIGATION_PARENT_MISSING", entry.id); break; }
      current = parent.parentId;
    }
  }
  return errors;
}

/** Shared frame for an unpublished release candidate and an actual publication. No timestamp,
 * receipt or authority is invented to validate a candidate. */
export function checkReleaseNavigation(frame: { bindingId: string; scope: Scope; revision: number; navigation: LocalNavigationDefinition }, refs: Ref[]): Diagnostic[] {
  const nav = frame.navigation, errors = checkNavigationReferences(nav, refs);
  if (nav.id !== frame.bindingId || nav.version !== frame.revision || !sameScope(nav.scope, frame.scope))
    errors.push({ code: "BINDING_MISMATCH", path: "navigation" });
  if (new Set(refs.map(ref => ref.id.toLowerCase())).size !== refs.length)
    errors.push({ code: "VERSION_CONFLICT", path: "surfaceVersionRefs" });
  if (refs.some(ref => !nav.entries.some(entry => sameRef(entry.surfaceRef, ref))))
    errors.push({ code: "SURFACE_UNAVAILABLE", path: "navigation" });
  return errors;
}
